import React, { useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/formatCoins';
import logoImage from '../../assets/logo.svg';

/**
 * Componente para gerar e imprimir o Termo de Autorização para Desconto em Folha
 * Baseado no modelo fornecido pelo usuário
 */
export const AuthorizationTerm = ({ 
  employee, 
  report, 
  onClose,
  onPrintAll = null,
  showPrintAllButton = false 
}) => {
  const printRef = useRef(null);

  useEffect(() => {
    // Quando o componente montar, focar na área de impressão
    if (printRef.current) {
      printRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  // Agrupar compras por data
  const purchasesByDate = {};
  if (report && report.orders) {
    report.orders.forEach((order) => {
      const dateKey = new Date(order.date).toLocaleDateString('pt-BR');
      if (!purchasesByDate[dateKey]) {
        purchasesByDate[dateKey] = 0;
      }
      purchasesByDate[dateKey] += parseFloat(order.totalCoins);
    });
  }

  // Formatar data atual
  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.toLocaleString('pt-BR', { month: 'long' });
  const currentYear = now.getFullYear();

  const periodStart = report?.period?.startDate
    ? dayjs(report.period.startDate).format('DD/MM/YYYY')
    : '-';
  const periodEnd = report?.period?.endDate
    ? dayjs(report.period.endDate).format('DD/MM/YYYY')
    : '-';
  const discrRows = Object.entries(purchasesByDate)
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => {
      const [da, ma, aa] = a.date.split('/').map(Number);
      const [db, mb, ab] = b.date.split('/').map(Number);
      return new Date(aa, ma - 1, da) - new Date(ab, mb - 1, db);
    });
  const half = Math.ceil(discrRows.length / 2);
  const leftRows = discrRows.slice(0, half);
  const rightRows = discrRows.slice(half);

  return (
    <>
      {/* Botões de ação - não aparecem na impressão */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{
            bgcolor: '#1e958c',
            '&:hover': { bgcolor: '#0b4f6c' },
          }}
        >
          Imprimir Termo
        </Button>
        {showPrintAllButton && onPrintAll && (
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={onPrintAll}
            sx={{
              borderColor: '#1e958c',
              color: '#1e958c',
              '&:hover': {
                borderColor: '#0b4f6c',
                bgcolor: 'rgba(30, 149, 140, 0.1)',
              },
            }}
          >
            Imprimir Todos os Colaboradores
          </Button>
        )}
        {onClose && (
          <Button variant="outlined" onClick={onClose}>
            Fechar
          </Button>
        )}
      </Box>

      {/* Conteúdo do termo - aparece na impressão */}
      <Box
        ref={printRef}
        className="print-content"
        sx={{
          width: '100%',
          maxWidth: '210mm', // A4 width
          margin: '0 auto',
          padding: '12mm',
          backgroundColor: 'white',
          '@media print': {
            padding: '10mm',
            maxWidth: '100%',
            height: 'auto',
            pageBreakAfter: 'avoid',
            pageBreakInside: 'avoid',
            breakInside: 'avoid-page',
            overflow: 'visible',
          },
        }}
      >
        {/* Header com logo e campos */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1.5,
            borderBottom: '2px solid #000',
            pb: 0.8,
            '@media print': {
              mb: 1.2,
              pb: 0.6,
            },
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '13px',
                '@media print': {
                  fontSize: '12px',
                },
              }}
            >
              Total: {formatCurrency(report?.summary?.totalMonth || 0)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '13px',
                '@media print': {
                  fontSize: '12px',
                },
              }}
            >
              Período: {periodStart} até {periodEnd}
            </Typography>
          </Box>
        </Box>

        {/* Logo/Título */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img 
              src={logoImage} 
              alt="Colégio Autêntico" 
              style={{ 
                maxWidth: '200px', 
                height: 'auto',
                '@media print': {
                  maxWidth: '180px',
                }
              }} 
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mt: 1,
              '@media print': {
                fontSize: '15px',
                mt: 0.8,
              },
            }}
          >
            TERMO DE AUTORIZAÇÃO PARA DESCONTO EM FOLHA DE PAGAMENTO
          </Typography>
        </Box>

        {/* Texto de autorização */}
        <Box sx={{ mb: 2, lineHeight: 1.6 }}>
          <Typography variant="body2" sx={{ textAlign: 'justify', mb: 1.2, fontSize: '12px', '@media print': { fontSize: '11px', mb: 1 } }}>
            Pelo presente instrumento, eu{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '280px',
                borderBottom: '1px solid #000',
                textAlign: 'center',
                fontWeight: 600,
                mx: 0.5,
                fontSize: '12px',
                py: 0.2,
                '@media print': {
                  minWidth: '260px',
                  fontSize: '11px',
                },
              }}
            >
              {employee?.name || '_________________________'}
            </Box>
            , inscrito (a) no CPF sob o nº{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '140px',
                borderBottom: '1px solid #000',
                textAlign: 'center',
                fontWeight: 600,
                mx: 0.5,
                fontSize: '12px',
                py: 0.2,
                '@media print': {
                  minWidth: '130px',
                  fontSize: '11px',
                },
              }}
            >
              {employee?.cpf || '________________'}
            </Box>
            , como funcionário(a) do Colégio Autêntico, autorizo a referida instituição a descontar
            mensalmente de minha folha de pagamento os valores referentes a consumos realizados por
            mim na cantina interna da empresa conforme discriminado abaixo, podendo tais verbas
            inclusive serem descontadas na minha rescisão.
            <br />
            <br />
            Esta autorização tem validade até que eu solicite o cancelamento da mesma através de
            solicitação feita de próprio punho e protocolada junto ao setor Recursos Humanos do
            Colégio Autêntico.
            <br />
            <br />
            Confirmo ter realizado o consumo junto à cantina da empresa no período de{' '}
            <strong>{periodStart}</strong> até <strong>{periodEnd}</strong>, totalizando{' '}
            <strong>{formatCurrency(report?.summary?.totalMonth || 0)}</strong>, e declaro ainda
            estar ciente de que tal valor será debitado em meu próximo salário.
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'right', mt: 1.2, fontSize: '12px', '@media print': { fontSize: '11px', mt: 1 } }}>
            Campina Grande - PB,{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '40px',
                borderBottom: '1px solid #000',
                textAlign: 'center',
                mx: 0.5,
                py: 0.2,
                '@media print': {
                  minWidth: '35px',
                },
              }}
            >
              {currentDay}
            </Box>
            de{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '120px',
                borderBottom: '1px solid #000',
                textAlign: 'center',
                mx: 0.5,
                py: 0.2,
                '@media print': {
                  minWidth: '110px',
                },
              }}
            >
              {currentMonth}
            </Box>
            de{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '60px',
                borderBottom: '1px solid #000',
                textAlign: 'center',
                mx: 0.5,
                py: 0.2,
                '@media print': {
                  minWidth: '55px',
                },
              }}
            >
              {currentYear}
            </Box>
            .
          </Typography>
        </Box>

        {/* Tabela de compras por dia */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              textAlign: 'center',
              fontSize: '13px',
              '@media print': {
                fontSize: '12px',
                mb: 0.8,
              },
            }}
          >
            DISCRIMINAÇÃO DAS COMPRAS
          </Typography>
          
          {/* Duas colunas lado a lado */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Coluna esquerda */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  border: '1px solid #000',
                  '& > *:not(:last-child)': {
                    borderBottom: '1px solid #000',
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    borderBottom: '2px solid #000',
                    fontWeight: 600,
                    fontSize: '11px',
                    '@media print': {
                      fontSize: '10px',
                    },
                  }}
                >
                  <Box sx={{ flex: 1, borderRight: '1px solid #000', p: 0.4, textAlign: 'center' }}>
                    DATA
                  </Box>
                  <Box sx={{ flex: 1, p: 0.4, textAlign: 'center' }}>
                    R$
                  </Box>
                </Box>
                {/* Linhas */}
                {(leftRows.length ? leftRows : [{ date: '-', amount: null }]).map((row, i) => {
                  return (
                    <Box
                      key={`${row.date}-${i}`}
                      sx={{
                        display: 'flex',
                        minHeight: '20px',
                        '@media print': {
                          minHeight: '18px',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          borderRight: '1px solid #000',
                          p: 0.3,
                          textAlign: 'center',
                          fontSize: '11px',
                          '@media print': {
                            fontSize: '10px',
                            p: 0.25,
                          },
                        }}
                      >
                        {row.date}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          p: 0.3,
                          textAlign: 'center',
                          fontSize: '11px',
                          '@media print': {
                            fontSize: '10px',
                            p: 0.25,
                          },
                        }}
                      >
                        {row.amount != null ? formatCurrency(row.amount) : ''}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Coluna direita */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  border: '1px solid #000',
                  '& > *:not(:last-child)': {
                    borderBottom: '1px solid #000',
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    borderBottom: '2px solid #000',
                    fontWeight: 600,
                    fontSize: '11px',
                    '@media print': {
                      fontSize: '10px',
                    },
                  }}
                >
                  <Box sx={{ flex: 1, borderRight: '1px solid #000', p: 0.4, textAlign: 'center' }}>
                    DATA
                  </Box>
                  <Box sx={{ flex: 1, p: 0.4, textAlign: 'center' }}>
                    R$
                  </Box>
                </Box>
                {/* Linhas */}
                {(rightRows.length ? rightRows : [{ date: '-', amount: null }]).map((row, i) => {
                  return (
                    <Box
                      key={`${row.date}-${i}`}
                      sx={{
                        display: 'flex',
                        minHeight: '20px',
                        '@media print': {
                          minHeight: '18px',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          borderRight: '1px solid #000',
                          p: 0.3,
                          textAlign: 'center',
                          fontSize: '11px',
                          '@media print': {
                            fontSize: '10px',
                            p: 0.25,
                          },
                        }}
                      >
                        {row.date}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          p: 0.3,
                          textAlign: 'center',
                          fontSize: '11px',
                          '@media print': {
                            fontSize: '10px',
                            p: 0.25,
                          },
                        }}
                      >
                        {row.amount != null ? formatCurrency(row.amount) : ''}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Espaço para assinatura */}
        <Box sx={{ mt: 4, textAlign: 'center', '@media print': { mt: 3 } }}>
          <Box
            sx={{
              borderTop: '1px solid #000',
              width: '280px',
              margin: '0 auto',
              pt: 0.8,
              '@media print': {
                width: '260px',
                pt: 0.6,
              },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, '@media print': { fontSize: '11px' } }}>
              {employee?.name || '_________________________'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '10px', color: '#666', display: 'block', mt: 0.5, '@media print': { fontSize: '9px' } }}>
              Assinatura do Colaborador
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Estilos para impressão */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: auto;
              overflow: visible;
              padding-top: 6mm !important;
              padding-bottom: 4mm !important;
            }
            @page {
              margin: 8mm;
              size: A4;
            }
            .print-content img {
              max-width: 160px !important;
              height: auto !important;
            }
          }
        `}
      </style>
    </>
  );
};
